import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { v4 as uuidv4 } from 'uuid';
import { DeviceInfo, SessionInfo, StandardTrackingProperties, UTMParameters } from '../models/tracking.model';

@Injectable({
  providedIn: 'root'
})
export class UtmTrackingService {
  private readonly UTM_STORAGE_KEY = 'verto_utm_params';
  private readonly REFERRER_STORAGE_KEY = 'verto_referrer';
  private readonly SESSION_STORAGE_KEY = 'verto_session_info';

  constructor(@Inject(PLATFORM_ID) private platformId: Object) {
    if (isPlatformBrowser(this.platformId)) {
      this.initializeTracking();
    }
  }

  // Initialize tracking by capturing UTM parameters and referrer on first load
  private initializeTracking(): void {
    // Capture UTM parameters from URL
    let utmParams = this.extractUTMFromURL();
    
    // If no UTM params in URL, auto-detect from referrer as fallback
    if (Object.keys(utmParams).length === 0 && document.referrer) {
      utmParams = this.detectUTMFromReferrer(document.referrer);
    }
    
    if (Object.keys(utmParams).length > 0) {
      this.saveUTMParameters(utmParams);
    }

    // Capture referrer on first visit
    if (!this.getReferrer() && document.referrer) {
      this.saveReferrer(document.referrer);
    }

    // Initialize or retrieve session
    this.initializeSession();
  }

  // Extract UTM parameters from current URL
  private extractUTMFromURL(): UTMParameters {
    const params = new URLSearchParams(window.location.search);
    const utmParams: UTMParameters = {};

    const source = params.get('utm_source');
    const medium = params.get('utm_medium');
    const campaign = params.get('utm_campaign');
    const content = params.get('utm_content');
    const term = params.get('utm_term');

    if (source) utmParams.source = source;
    if (medium) utmParams.medium = medium;
    if (campaign) utmParams.campaign = campaign;
    if (content) utmParams.content = content;
    if (term) utmParams.term = term;

    return utmParams;
  }

  /**
   * Auto-detect UTM parameters from referrer URL as fallback
   * Used when no explicit UTM parameters are present in the URL
   */
  private detectUTMFromReferrer(referrerUrl: string): UTMParameters {
    try {
      const url = new URL(referrerUrl);
      const hostname = url.hostname.toLowerCase();
      const utmParams: UTMParameters = {};

      const verifyHostname = (domain: string): boolean => {
        return hostname === domain || hostname.endsWith('.' + domain);
      };

      // Social Media Platforms
      if (verifyHostname('linkedin.com')) {
        utmParams.source = 'linkedin';
        utmParams.medium = 'social';
      } else if (verifyHostname('facebook.com') || verifyHostname('fb.com')) {
        utmParams.source = 'facebook';
        utmParams.medium = 'social';
      } else if (verifyHostname('twitter.com') || verifyHostname('t.co')) {
        utmParams.source = 'twitter';
        utmParams.medium = 'social';
      } else if (verifyHostname('instagram.com')) {
        utmParams.source = 'instagram';
        utmParams.medium = 'social';
      } else if (verifyHostname('youtube.com')) {
        utmParams.source = 'youtube';
        utmParams.medium = 'video';
      } else if (verifyHostname('reddit.com')) {
        utmParams.source = 'reddit';
        utmParams.medium = 'social';
      } else if (verifyHostname('tiktok.com')) {
        utmParams.source = 'tiktok';
        utmParams.medium = 'social';
      }
      // Search Engines
      else if (verifyHostname('google.com') || hostname.includes('google.') || verifyHostname('bing.com')) {
        const searchEngine = hostname.includes('google') ? 'google' : 'bing';
        utmParams.source = searchEngine;
        // Check if it's paid (has ad-related params) or organic
        const isPaid = url.searchParams.has('gclid') || url.searchParams.has('msclkid');
        utmParams.medium = isPaid ? 'cpc' : 'organic';
      } else if (verifyHostname('yahoo.com') || verifyHostname('duckduckgo.com')) {
        utmParams.source = hostname.includes('yahoo') ? 'yahoo' : 'duckduckgo';
        utmParams.medium = 'organic';
      }
      // Email Providers (may indicate email campaign)
      else if (verifyHostname('mail.google.com') || hostname.includes('outlook.')) {
        utmParams.source = hostname.includes('google') ? 'gmail' : 'outlook';
        utmParams.medium = 'email';
      }
      // Other known referrers
      else if (verifyHostname('medium.com') || verifyHostname('substack.com')) {
        utmParams.source = hostname.includes('medium') ? 'medium' : 'substack';
        utmParams.medium = 'referral';
      }
      // Generic referral for any other external site
      else if (!verifyHostname('verto.co') && !verifyHostname('vertofx.com')) {
        utmParams.source = hostname.replace('www.', '');
        utmParams.medium = 'referral';
      }

      return utmParams;
    } catch (error) {
      console.error('Error detecting UTM from referrer:', error);
      return {};
    }
  }

  // Save UTM parameters to sessionStorage
  private saveUTMParameters(params: UTMParameters): void {
    try {
      sessionStorage.setItem(this.UTM_STORAGE_KEY, JSON.stringify(params));
    } catch (error) {
      console.error('Error saving UTM parameters:', error);
    }
  }

  // Get saved UTM parameters from sessionStorage
  getUTMParameters(): UTMParameters {
    try {
      const stored = sessionStorage.getItem(this.UTM_STORAGE_KEY);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.error('Error retrieving UTM parameters:', error);
      return {};
    }
  }

  // Save referrer to sessionStorage
  private saveReferrer(referrer: string): void {
    try {
      sessionStorage.setItem(this.REFERRER_STORAGE_KEY, referrer);
    } catch (error) {
      console.error('Error saving referrer:', error);
    }
  }

  // Get saved referrer from sessionStorage
  getReferrer(): string | null {
    try {
      return sessionStorage.getItem(this.REFERRER_STORAGE_KEY);
    } catch (error) {
      console.error('Error retrieving referrer:', error);
      return null;
    }
  }

  // Detect device type from user agent
  getDeviceInfo(): DeviceInfo {
    const userAgent = navigator.userAgent.toLowerCase();
    
    // Detect device type
    let type: 'desktop' | 'mobile' | 'tablet' = 'desktop';
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      type = 'tablet';
    } else if (/mobile|iphone|ipod|android|blackberry|opera mini|windows phone/i.test(userAgent)) {
      type = 'mobile';
    }

    // Detect browser
    let browser = 'Unknown';
    if (userAgent.includes('firefox')) {
      browser = 'Firefox';
    } else if (userAgent.includes('edg')) {
      browser = 'Edge';
    } else if (userAgent.includes('chrome')) {
      browser = 'Chrome';
    } else if (userAgent.includes('safari')) {
      browser = 'Safari';
    } else if (userAgent.includes('opera') || userAgent.includes('opr')) {
      browser = 'Opera';
    }

    return { type, browser };
  }

  // Initialize or retrieve session information
  private initializeSession(): void {
    let session = this.getSessionInfo();
    
    if (!session) {
      // Create new session
      session = {
        id: uuidv4(),
        startTime: new Date().toISOString(),
        pagesViewed: 0
      };
      this.saveSessionInfo(session);
    }
  }

  // Get session information
  getSessionInfo(): SessionInfo | null {
    try {
      const stored = sessionStorage.getItem(this.SESSION_STORAGE_KEY);
      return stored ? JSON.parse(stored) : null;
    } catch (error) {
      console.error('Error retrieving session info:', error);
      return null;
    }
  }

  // Save session information
  private saveSessionInfo(session: SessionInfo): void {
    try {
      sessionStorage.setItem(this.SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch (error) {
      console.error('Error saving session info:', error);
    }
  }

  // Increment page view count for current session
  incrementPageView(): void {
    const session = this.getSessionInfo();
    if (session) {
      session.pagesViewed++;
      this.saveSessionInfo(session);
    }
  }

  // Get all standard tracking properties for events
  getStandardProperties(): StandardTrackingProperties {
    const utmParams = this.getUTMParameters();
    const deviceInfo = this.getDeviceInfo();
    const session = this.getSessionInfo();
    const referrer = this.getReferrer();

    return {
      // Marketing Attribution
      utm_source: utmParams.source,
      utm_medium: utmParams.medium,
      utm_campaign: utmParams.campaign,
      utm_content: utmParams.content,
      utm_term: utmParams.term,
      referrer: referrer || undefined,

      // Context
      device_type: deviceInfo.type,
      browser: deviceInfo.browser,
      page_url: window.location.href,
      page_title: document.title,
      timestamp: new Date().toISOString(),

      // Session
      session_id: session?.id,
      session_start_time: session?.startTime,
      pages_viewed_in_session: session?.pagesViewed
    };
  }

  // Clear all tracking data (useful for testing or privacy)
  clearTrackingData(): void {
    try {
      sessionStorage.removeItem(this.UTM_STORAGE_KEY);
      sessionStorage.removeItem(this.REFERRER_STORAGE_KEY);
      sessionStorage.removeItem(this.SESSION_STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing tracking data:', error);
    }
  }
}
